/**
 * ConfirmModal - Warning / confirmation dialog matching ModalShell layout and Tailwind tokens
 * (void, charcoal, shadow, ether, energy). Use for destructive or consequential actions instead
 * of window.confirm.
 */

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

function ConfirmModal({
  open,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'danger',
  onConfirm,
  onCancel,
}) {
  const [internalBusy, setInternalBusy] = useState(false);
  const busy = internalBusy;

  useEffect(() => {
    if (!open) return undefined;
    const handleEscape = (e) => {
      if (e.key === 'Escape' && !busy) onCancel?.();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, busy, onCancel]);

  if (!open) return null;

  const confirmClass =
    variant === 'danger'
      ? 'bg-energy text-void font-medium hover:opacity-90 disabled:opacity-50'
      : 'bg-ether text-void font-medium hover:opacity-90 disabled:opacity-50';

  const handleConfirmClick = async () => {
    if (busy) return;
    setInternalBusy(true);
    try {
      await onConfirm?.();
    } finally {
      setInternalBusy(false);
    }
  };

  const modal = (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="confirm-modal-title"
      aria-describedby="confirm-modal-description"
      data-testid="confirm-modal"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className="absolute inset-0 bg-void/80 backdrop-blur-sm"
        onClick={() => !busy && onCancel?.()}
        aria-hidden="true"
      />
      <div className="relative w-full max-w-md bg-charcoal rounded-2xl border border-shadow shadow-soft p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 id="confirm-modal-title" className="font-display text-xl font-bold text-ash pr-2">
            {title}
          </h2>
          <button
            type="button"
            onClick={() => !busy && onCancel?.()}
            className="text-silver hover:text-ash text-2xl leading-none shrink-0"
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <p id="confirm-modal-description" className="text-silver text-sm mb-6">
          {description}
        </p>
        <div className="flex justify-end gap-2 flex-wrap">
          <button
            type="button"
            data-testid="confirm-modal-cancel"
            disabled={busy}
            className="px-4 py-2 rounded-xl border border-shadow text-silver hover:text-ash hover:bg-shadow/50 disabled:opacity-50 transition-colors"
            onClick={() => onCancel?.()}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            data-testid="confirm-modal-confirm"
            disabled={busy}
            className={`px-4 py-2 rounded-xl transition-colors disabled:opacity-50 ${confirmClass}`}
            onClick={handleConfirmClick}
          >
            {busy ? 'Please wait…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return modal;
  return createPortal(modal, document.body);
}

export default ConfirmModal;
